import type { GetWebcastSecurityQuery, UpdateSingleSignOnPolicyInput } from 'src/generated/graphql-manager';

import type { RestrictionType } from '../whitelist-modal/sso-whitelist-type-modal';

import {
  Accordion,
  Checkbox,
  PlusIcon,
  SecondaryButton,
  SelectionInputLayout,
  Toggle,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ItemListAction, useUpdateSingleSignOnPolicyMutation } from 'src/generated/graphql-manager';
import { useGetFeatures } from 'src/manager/hooks/use-get-features';

import { Whitelist } from '../whitelist';
import { SsoWhitelistTypeModal } from '../whitelist-modal';

import styles from '../event-access.module.css';

type SecurityPolicyUnion = GetWebcastSecurityQuery['webcast']['securityPolicies'][number];

type Props = {
  singleSignOnPolicy?: Extract<SecurityPolicyUnion, { __typename?: 'SingleSignOnPolicy' }>;
  idps?: GetWebcastSecurityQuery['identityProviders'];
  formDisabled?: boolean;
  refetch: () => void;
};

export function SingleSignOnPolicy({ singleSignOnPolicy, idps, refetch, formDisabled }: Props) {
  const { t } = useTranslation();
  const { webcastId = '' } = useParams();
  const { isWatermarkingFeatureEnabled } = useGetFeatures();

  const [ssoWhitelistTypeModalOpen, setSsoWhitelistTypeModalOpen] = useState(false);

  const singleSignOnEnabled = singleSignOnPolicy?.enabled === true;
  const singleSignOnRestrictionsEnabled = singleSignOnPolicy?.restrictions?.enabled === true;
  const ssoWhitelistedEmails = [
    ...(singleSignOnPolicy?.restrictions?.emailList.__typename === 'EmailList'
      ? singleSignOnPolicy?.restrictions?.emailList.emails
      : []),
    ...(singleSignOnPolicy?.restrictions?.emailTemplates ?? []),
  ];
  const ssoWhitelistedIds = singleSignOnPolicy?.restrictions?.employeeIds || [];
  const ssoWhitelistedGroups = singleSignOnPolicy?.restrictions?.groups || [];
  const showWatermarking = singleSignOnPolicy?.showWatermarking || false;
  const numberOfAttendees = ssoWhitelistedEmails.length + ssoWhitelistedIds.length + ssoWhitelistedGroups.length;

  const [updateSingleSignOnPolicyMutation, { loading: updateSingleSignOnPolicyLoading }] =
    useUpdateSingleSignOnPolicyMutation();

  const updateSingleSignOnPolicy = (input: UpdateSingleSignOnPolicyInput) => {
    if (updateSingleSignOnPolicyLoading) return;

    updateSingleSignOnPolicyMutation({
      variables: {
        input: {
          ...input,
          webcastId,
          idps: idps?.map(({ alias }) => alias),
          showWatermarking: input.showWatermarking ?? null,
        },
      },
      onCompleted: (data) => {
        refetch();

        if (data?.updateSingleSignOnPolicy.__typename === 'UpdateSingleSignOnPolicySuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const addSsoItems = (type: RestrictionType, items: string[]) => {
    if (updateSingleSignOnPolicyLoading) return;

    setSsoWhitelistTypeModalOpen(false);

    updateSingleSignOnPolicyMutation({
      variables: {
        input: {
          webcastId,
          [type]: [
            {
              action: ItemListAction.ADD,
              items: type === 'emails' ? items.map((item) => item.toLowerCase()) : items,
            },
          ],
        },
      },
      onCompleted: (data) => {
        refetch();

        if (data?.updateSingleSignOnPolicy.__typename === 'UpdateSingleSignOnPolicySuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const removeSsoItems = (type: RestrictionType, items: string[]) => {
    if (updateSingleSignOnPolicyLoading) return;

    updateSingleSignOnPolicyMutation({
      variables: {
        input: {
          webcastId,
          [type]: [
            {
              action: ItemListAction.REMOVE,
              items,
            },
          ],
        },
      },
      onCompleted: (data) => {
        refetch();

        if (data?.updateSingleSignOnPolicy.__typename === 'UpdateSingleSignOnPolicySuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  return (
    <>
      <SelectionInputLayout
        variant="ghost"
        checked={singleSignOnEnabled}
        className={styles.ssoToggleWrapper}
        disabled={formDisabled}
        hintMessage={t('manager.webcastSetup.eventAccess.accessControlMethods.sso.hint')}
        inputElement={
          <Checkbox
            id="single-sign-on-policy"
            data-testid="single-sign-on-policy-checkbox"
            checked={singleSignOnEnabled}
            disabled={formDisabled}
            onChange={() => updateSingleSignOnPolicy({ webcastId, enabled: !singleSignOnEnabled })}
          >
            {t('manager.webcastSetup.eventAccess.accessControlMethods.sso.label')}
          </Checkbox>
        }
      >
        {isWatermarkingFeatureEnabled && (
          <SelectionInputLayout
            hintMessage={t('manager.webcastSetup.eventAccess.accessControlMethods.sso.watermark.hint')}
            checked={showWatermarking}
            inputElement={
              <Toggle
                data-testid="watermark-toggle"
                checked={showWatermarking}
                onChange={() => updateSingleSignOnPolicy({ webcastId, showWatermarking: !showWatermarking })}
              >
                {t('manager.webcastSetup.eventAccess.accessControlMethods.sso.watermark.label')}
              </Toggle>
            }
          />
        )}

        <SelectionInputLayout
          checked={singleSignOnRestrictionsEnabled}
          inputElement={
            <Toggle
              data-testid="single-sign-on-restrictions-toggle"
              checked={singleSignOnRestrictionsEnabled}
              onChange={() =>
                updateSingleSignOnPolicy({
                  webcastId,
                  restrictionsEnabled: !singleSignOnRestrictionsEnabled,
                })
              }
            >
              {t('manager.webcastSetup.eventAccess.accessControlMethods.sso.restrictions')}
            </Toggle>
          }
        >
          <div className={styles.attendeesWrapper}>
            <SecondaryButton
              data-testid="sso-add-attendees-button"
              type="button"
              small
              onClick={() => setSsoWhitelistTypeModalOpen(true)}
            >
              {t('manager.webcastSetup.eventAccess.accessControlMethods.sso.addAttendees')}
              <PlusIcon />
            </SecondaryButton>

            {numberOfAttendees === 0 && (
              <span className={styles.accordionLabel} data-testid="sso-attendees-count">
                {t('manager.webcastSetup.eventAccess.accessControlMethods.sso.attendees', {
                  count: numberOfAttendees,
                })}
              </span>
            )}

            {numberOfAttendees > 0 && (
              <Accordion
                data-testid="sso-attendees-accordion"
                className={styles.accordionAttendees}
                buttonContent={
                  <span className={styles.accordionLabel}>
                    {t('manager.webcastSetup.eventAccess.accessControlMethods.sso.attendees', {
                      count: numberOfAttendees,
                    })}
                  </span>
                }
              >
                <Whitelist
                  type="emails"
                  items={ssoWhitelistedEmails}
                  onRemove={(item) => removeSsoItems('emails', [item])}
                  onRemoveAll={() => removeSsoItems('emails', ssoWhitelistedEmails)}
                />

                <Whitelist
                  type="employeeIds"
                  items={ssoWhitelistedIds}
                  onRemove={(item) => removeSsoItems('employeeIds', [item])}
                  onRemoveAll={() => removeSsoItems('employeeIds', ssoWhitelistedIds)}
                />

                <Whitelist
                  type="groups"
                  items={ssoWhitelistedGroups}
                  onRemove={(item) => removeSsoItems('groups', [item])}
                  onRemoveAll={() => removeSsoItems('groups', ssoWhitelistedGroups)}
                />
              </Accordion>
            )}
          </div>
        </SelectionInputLayout>
      </SelectionInputLayout>

      <SsoWhitelistTypeModal
        isOpen={ssoWhitelistTypeModalOpen}
        onConfirm={addSsoItems}
        onClose={() => setSsoWhitelistTypeModalOpen(false)}
      />
    </>
  );
}
